import { useRef, useState } from 'react';

const ANIM_CSS = `
@keyframes pricing-fade-in {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes pricing-fade-in-delay {
  0%, 30% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}
.pricing-header-anim {
  animation: pricing-fade-in 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.pricing-card-free:hover {
  transform: translateY(-6px) !important;
  border-color: rgba(255,255,255,0.2) !important;
  box-shadow: 0 12px 48px rgba(0,0,0,0.3) !important;
}
.pricing-card-pro:hover {
  transform: translateY(-6px) !important;
  border-color: rgba(124,58,237,0.5) !important;
  box-shadow: 0 12px 48px rgba(124,58,237,0.2) !important;
}
.pricing-free-btn:hover {
  background: rgba(255,255,255,0.1) !important;
}
.pricing-pro-btn:hover {
  transform: scale(1.02) !important;
  box-shadow: 0 6px 24px rgba(124,58,237,0.35) !important;
}
.pricing-pro-btn:active {
  transform: scale(0.98) !important;
}
.pricing-faq-item:hover {
  border-color: rgba(124,58,237,0.3) !important;
  background: rgba(255,255,255,0.04) !important;
}
@media (max-width: 768px) {
  .pricing-grid { flex-direction: column !important; align-items: center !important; }
  .pricing-faq-grid { grid-template-columns: 1fr !important; }
}
`;

const FREE_FEATURES = [
  'Paste any text or script',
  'Keyword extraction & ranking',
  'Readability scoring',
  'Power word detection',
  'Topic timeline visualization',
];

const PRO_FEATURES = [
  'Everything in Free',
  'YouTube URL transcript analysis',
  'AI-generated title suggestions (3 categories)',
  'CTR Simulator — test titles before publishing',
  'Sentiment & tone analysis',
  'Best quotes extraction',
  'Opening hook suggestions',
  'AI video summary',
  'SEO tag generation',
  'YouTube description generator',
  'Analysis history & cache',
];

const FAQ_ITEMS = [
  { q: 'Can I use Titler without signing up?', a: 'Yes. Text analysis is completely free with no account required. Just paste your content and analyze.' },
  { q: 'What does Pro include?', a: 'Pro unlocks YouTube URL analysis, AI-powered titles in 3 categories, CTR Simulator, sentiment analysis, description generation, SEO tags, and saved history.' },
  { q: 'Can I cancel anytime?', a: 'Absolutely. Cancel your subscription at any time with no questions asked.' },
  { q: 'How does YouTube URL analysis work?', a: 'Paste any YouTube video URL and we automatically fetch the transcript, then run full keyword and AI analysis on it.' },
  { q: 'What is the CTR Simulator?', a: 'The CTR Simulator lets you test title variations against simulated viewer archetypes before publishing. See predicted click-through rates, strengths, weaknesses, and AI-suggested rewrites.' },
];

const PRO_PRICE = '15';
const PRO_PERIOD = 'mo';

const CheckIcon = ({ color = '#94a3b8' }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill={color} style={{ flexShrink: 0, marginTop: '2px' }}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

// Set to false to hide the free credits banner
const SHOW_FREE_CREDITS_BANNER = true;

const PricingPage = ({ onFreeStart, onProStart }) => {
  const cardsRef = useRef(null);
  const faqRef = useRef(null);
  const cardsVisible = true;
  const faqVisible = true;
  const [bannerDismissed, setBannerDismissed] = useState(false);

  return (
    <div style={styles.page}>
      <style>{ANIM_CSS}</style>

      {/* Background orbs */}
      <div style={styles.orb1} />
      <div style={styles.orb2} />

      {/* Free credits promo banner */}
      {SHOW_FREE_CREDITS_BANNER && !bannerDismissed && (
        <div style={styles.promoBanner}>
          <div style={styles.promoBannerInner}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#fbbf24" style={{ flexShrink: 0 }}>
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
            </svg>
            <span style={styles.promoBannerText}>
              Sign up today and get <strong style={{ color: '#fbbf24' }}>3 free AI credits</strong> to try every Pro feature — no credit card needed.
            </span>
            <button
              onClick={() => setBannerDismissed(true)}
              style={styles.promoBannerClose}
              aria-label="Dismiss"
            >&times;</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header} className="pricing-header-anim">
        <h1 style={styles.title}>Simple, transparent pricing</h1>
        <p style={styles.subtitle}>Start analyzing for free. Upgrade to Pro when you need the full AI toolkit.</p>
      </div>

      {/* Cards */}
      <div
        ref={cardsRef}
        className="pricing-grid"
        style={{
          ...styles.grid,
          opacity: cardsVisible ? 1 : 0,
          transform: cardsVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Free Tier */}
        <div style={styles.card} className="pricing-card-free">
          <div style={styles.tierName}>Free</div>
          <div style={styles.priceRow}>
            <span style={styles.price}>$0</span>
            <span style={styles.period}>/forever</span>
          </div>
          <p style={styles.tierDesc}>Everything you need to analyze text and find the right keywords.</p>
          <button onClick={onProStart} style={styles.freeBtn} className="pricing-free-btn">
            Get Started
          </button>
          <div style={styles.divider} />
          <ul style={styles.featureList}>
            {FREE_FEATURES.map((f, i) => (
              <li key={f} style={{
                ...styles.featureItem,
                opacity: cardsVisible ? 1 : 0,
                transform: cardsVisible ? 'translateX(0)' : 'translateX(-12px)',
                transition: `opacity 0.4s ease ${0.3 + i * 0.06}s, transform 0.4s ease ${0.3 + i * 0.06}s`,
              }}>
                <CheckIcon />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pro Tier */}
        <div style={{ ...styles.card, ...styles.proCard }} className="pricing-card-pro">
          <div style={styles.badge}>Most Popular</div>
          <div style={{ ...styles.tierName, color: '#c4b5fd' }}>Pro</div>
          <div style={styles.priceRow}>
            <span style={styles.price}>${PRO_PRICE}</span>
            <span style={styles.period}>/{PRO_PERIOD}</span>
          </div>
          <p style={styles.tierDesc}>Full AI-powered analysis for serious YouTube creators.</p>
          <button onClick={onProStart} style={styles.proBtn} className="pricing-pro-btn">
            Upgrade to Pro
          </button>
          <div style={styles.divider} />
          <ul style={styles.featureList}>
            {PRO_FEATURES.map((f, i) => (
              <li key={f} style={{
                ...styles.featureItem,
                opacity: cardsVisible ? 1 : 0,
                transform: cardsVisible ? 'translateX(0)' : 'translateX(-12px)',
                transition: `opacity 0.4s ease ${0.3 + i * 0.06}s, transform 0.4s ease ${0.3 + i * 0.06}s`,
              }}>
                <CheckIcon color="#a78bfa" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Comparison highlight */}
      <div
        style={{
          ...styles.compareRow,
          opacity: cardsVisible ? 1 : 0,
          transform: cardsVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s ease 0.5s, transform 0.7s ease 0.5s',
        }}
      >
        <div style={styles.compareCard}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="#a78bfa" style={{ flexShrink: 0 }}>
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
          </svg>
          <span style={styles.compareText}>No credit card required for Free tier</span>
        </div>
        <div style={styles.compareCard}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="#a78bfa" style={{ flexShrink: 0 }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <span style={styles.compareText}>Cancel Pro anytime, no questions asked</span>
        </div>
      </div>

      {/* FAQ Section */}
      <div
        ref={faqRef}
        style={{
          ...styles.faqSection,
          opacity: faqVisible ? 1 : 0,
          transform: faqVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <h2 style={styles.faqTitle}>Frequently asked questions</h2>
        <div className="pricing-faq-grid" style={styles.faqGrid}>
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} style={{
              ...styles.faqItem,
              opacity: faqVisible ? 1 : 0,
              transform: faqVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: `opacity 0.5s ease ${i * 0.1}s, transform 0.5s ease ${i * 0.1}s`,
            }} className="pricing-faq-item">
              <h4 style={styles.faqQ}>{item.q}</h4>
              <p style={styles.faqA}>{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={styles.bottomCta}>
        <p style={styles.bottomText}>Ready to upgrade? Start optimizing your titles today.</p>
        <button onClick={onProStart} style={styles.proBtn} className="pricing-pro-btn">
          Upgrade to Pro — ${PRO_PRICE}/{PRO_PERIOD}
        </button>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#000000',
    padding: '80px 20px 60px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#e2e8f0',
    position: 'relative',
    overflow: 'clip',
  },
  orb1: {
    position: 'absolute', top: '-5%', right: '-10%',
    width: '500px', height: '500px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(124,58,237,0.12) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 0,
  },
  orb2: {
    position: 'absolute', top: '50%', left: '-10%',
    width: '400px', height: '400px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 0,
  },
  header: {
    textAlign: 'center', marginBottom: '56px', position: 'relative', zIndex: 1,
  },
  title: {
    margin: '0 0 14px', fontSize: '44px', fontWeight: '800',
    letterSpacing: '-2px', lineHeight: '1.1',
    background: 'linear-gradient(135deg, #e2e8f0 0%, #a78bfa 60%, #818cf8 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    margin: 0, fontSize: '17px', color: '#64748b', lineHeight: '1.6',
    maxWidth: '440px', marginLeft: 'auto', marginRight: 'auto',
  },
  grid: {
    display: 'flex', gap: '24px', justifyContent: 'center',
    flexWrap: 'wrap', maxWidth: '820px', margin: '0 auto',
    position: 'relative', zIndex: 1,
  },
  card: {
    flex: '1 1 340px', maxWidth: '380px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '20px', padding: '36px 30px',
    position: 'relative',
    transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
  },
  proCard: {
    border: '1px solid rgba(124,58,237,0.35)',
    background: 'rgba(124,58,237,0.05)',
  },
  badge: {
    position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)',
    padding: '5px 18px', borderRadius: '20px',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '12px', fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
    boxShadow: '0 4px 16px rgba(124,58,237,0.3)',
  },
  tierName: {
    fontSize: '20px', fontWeight: '700', color: '#e2e8f0', marginBottom: '8px',
  },
  priceRow: {
    display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '14px',
  },
  price: {
    fontSize: '48px', fontWeight: '800', color: '#e2e8f0', letterSpacing: '-2px',
  },
  period: {
    fontSize: '16px', color: '#475569', fontWeight: '500',
  },
  tierDesc: {
    margin: '0 0 24px', fontSize: '14px', color: '#94a3b8', lineHeight: '1.6',
  },
  freeBtn: {
    width: '100%', padding: '13px 20px', borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#e2e8f0', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'background 0.2s ease',
  },
  proBtn: {
    width: '100%', maxWidth: '380px', padding: '13px 20px', borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 4px 16px rgba(124,58,237,0.2)',
  },
  divider: {
    height: '1px', background: 'rgba(255,255,255,0.06)', margin: '24px 0',
  },
  featureList: {
    listStyle: 'none', margin: 0, padding: 0,
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  featureItem: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    fontSize: '14px', color: '#cbd5e1', lineHeight: '1.4',
  },
  // Compare row
  compareRow: {
    display: 'flex', gap: '16px', justifyContent: 'center',
    flexWrap: 'wrap', maxWidth: '820px', margin: '32px auto 0',
    position: 'relative', zIndex: 1,
  },
  compareCard: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 20px', borderRadius: '12px',
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
  },
  compareText: {
    fontSize: '13px', color: '#94a3b8',
  },
  // FAQ
  faqSection: {
    maxWidth: '820px', margin: '80px auto 0',
    position: 'relative', zIndex: 1,
  },
  faqTitle: {
    fontSize: '28px', fontWeight: '800', color: '#e2e8f0',
    textAlign: 'center', marginBottom: '32px', marginTop: 0,
    letterSpacing: '-1px',
  },
  faqGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px',
  },
  faqItem: {
    padding: '24px', borderRadius: '16px',
    background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
    transition: 'border-color 0.2s ease, background 0.2s ease',
  },
  faqQ: {
    margin: '0 0 8px', fontSize: '15px', fontWeight: '600', color: '#e2e8f0',
  },
  faqA: {
    margin: 0, fontSize: '13px', color: '#94a3b8', lineHeight: '1.6',
  },
  // Bottom CTA
  bottomCta: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    marginTop: '80px', position: 'relative', zIndex: 1,
  },
  bottomText: {
    fontSize: '16px', color: '#64748b', marginBottom: '16px', marginTop: 0,
  },
  // Promo banner
  promoBanner: {
    maxWidth: '640px', margin: '0 auto 32px', position: 'relative', zIndex: 1,
  },
  promoBannerInner: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 20px', borderRadius: '14px',
    background: 'rgba(251, 191, 36, 0.08)',
    border: '1px solid rgba(251, 191, 36, 0.2)',
  },
  promoBannerText: {
    flex: 1, fontSize: '14px', color: '#e2e8f0', lineHeight: '1.5',
  },
  promoBannerClose: {
    background: 'none', border: 'none', color: '#64748b', fontSize: '20px',
    cursor: 'pointer', padding: '0 4px', lineHeight: 1, flexShrink: 0,
  },
};

export default PricingPage;
